import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { PaletteIcon, ChartIcon, TeamIcon, TrendingUpIcon } from '../components/Icons';
import './ClientRequest.css';

const ClientRequest = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    serviceType: '',
    otherService: '',
    location: '',
    businessType: '',
    currentSituation: '',
    dailyVolume: '',
    budget: '',
    timeline: '',
    additionalNotes: '',
    preferredContact: '',
    hearAboutUs: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setSubmitStatus('success');
      setIsSubmitting(false);
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        serviceType: '',
        otherService: '',
        location: '',
        businessType: '',
        currentSituation: '',
        dailyVolume: '',
        budget: '',
        timeline: '',
        additionalNotes: '',
        preferredContact: '',
        hearAboutUs: ''
      });
    }, 2000);
  };

  return (
    <div className="client-request-page">
      <Helmet>
        <title>Get Custom Quote - Water Business Solution Request</title>
        <meta name="description" content="Request a custom quote for your water business solution. Get expert consultation and tailored recommendations from Pure Drop Technicals." />
      </Helmet>
      
      {/* Hero Section */}
      <section className="request-hero section small-hero">
        <div className="container">
          <div className="request-hero-content">
            <div className="request-text">
              <h1>Get Your Custom Water Solution Quote</h1>
              <p className="lead">
                Tell us about your project and we’ll design the perfect water business setup for your budget.
              </p>
              <div className="hero-cta">
                <a href="#request-form" className="btn btn-primary">Start Request</a>
                <a href="/contact" className="btn btn-outline">Speak To Us</a>
              </div>
            </div>
            <div className="request-visual" aria-hidden="true">
              <div className="request-icon-badge">
                <TrendingUpIcon size={56} />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Request Form */}
      <section id="request-form" className="request-form-section section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-intro">
              <h2>Request Information</h2>
              <p>Please provide as much detail as possible so we can give you the most accurate quote and recommendations.</p>
              
              <div className="process-steps">
                <div className="step">
                  <span className="step-number">1</span>
                  <span className="step-text">Submit Request</span>
                </div>
                <div className="step">
                  <span className="step-number">2</span>
                  <span className="step-text">Expert Review</span>
                </div>
                <div className="step">
                  <span className="step-number">3</span>
                  <span className="step-text">Custom Proposal</span>
                </div>
                <div className="step">
                  <span className="step-number">4</span>
                  <span className="step-text">Consultation Call</span>
                </div>
              </div>
            </div>
            
            {submitStatus === 'success' ? (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h3>Request Submitted Successfully!</h3>
                <p>Thank you for your interest in Pure Drop Technicals. Our water business experts will review your request and contact you within 24 hours with a custom proposal.</p>
                
                <div className="next-steps">
                  <h4>What happens next:</h4>
                  <ul>
                    <li>✓ Expert review of your requirements (within 2 hours)</li>
                    <li>✓ Custom solution design and quote preparation</li>
                    <li>✓ Personal consultation call scheduled</li>
                    <li>✓ Detailed proposal with ROI analysis sent</li>
                  </ul>
                </div>
                
                <div className="emergency-contact">
                  <p>Need immediate assistance? <br/>
                  <strong>WhatsApp:</strong> +27 12 345 6789 <br/>
                  <strong>Email:</strong> info@watershopinstallations.co.za</p>
                </div>
              </div>
            ) : (
              <form className="request-form" onSubmit={handleSubmit}>
                {/* Personal Information */}
                <div className="form-section">
                  <h3>Personal Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+27 12 345 6789"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                {/* Service Requirements */}
                <div className="form-section">
                  <h3>Service Requirements</h3>
                  <div className="form-group">
                    <label htmlFor="serviceType">Primary Service Interest *</label>
                    <select
                      id="serviceType"
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Service Type</option>
                      <option value="water-shop">Water Shop Installation</option>
                      <option value="commercial-plant">Commercial Water Purification Plant</option>
                      <option value="alkaline-system">Alkaline Water System</option>
                      <option value="containerized">Containerized Water System</option>
                      <option value="maintenance">Maintenance & Support</option>
                      <option value="upgrade">System Upgrade/Expansion</option>
                      <option value="other">Other (please specify)</option>
                    </select>
                  </div>
                  
                  {formData.serviceType === 'other' && (
                    <div className="form-group">
                      <label htmlFor="otherService">Please specify your service needs *</label>
                      <input
                        type="text"
                        id="otherService"
                        name="otherService"
                        value={formData.otherService}
                        onChange={handleInputChange}
                        required={formData.serviceType === 'other'}
                      />
                    </div>
                  )}
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="location">Location (City/Town) *</label>
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        placeholder="e.g., Johannesburg, Cape Town"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="businessType">Business Type</label>
                      <select
                        id="businessType"
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Business Type</option>
                        <option value="new-startup">New Startup</option>
                        <option value="existing-business">Existing Business Expansion</option>
                        <option value="franchise">Franchise/Chain</option>
                        <option value="community">Community/Municipal</option>
                        <option value="industrial">Industrial/Manufacturing</option>
                        <option value="hospitality">Hospitality/Hotels</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* Business Details */}
                <div className="form-section">
                  <h3>Business Details</h3>
                  <div className="form-group">
                    <label htmlFor="currentSituation">Current Situation</label>
                    <select
                      id="currentSituation"
                      name="currentSituation"
                      value={formData.currentSituation}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Current Situation</option>
                      <option value="planning-stage">Planning Stage - No Equipment</option>
                      <option value="have-location">Have Location/Site Ready</option>
                      <option value="existing-system">Have Existing Water System</option>
                      <option value="upgrading">Looking to Upgrade Current System</option>
                      <option value="emergency">Emergency Replacement Needed</option>
                    </select>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="dailyVolume">Expected Daily Water Volume</label>
                      <select
                        id="dailyVolume"
                        name="dailyVolume"
                        value={formData.dailyVolume}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Daily Volume</option>
                        <option value="under-500">Under 500 litres</option>
                        <option value="500-1000">500 - 1,000 litres</option>
                        <option value="1000-2000">1,000 - 2,000 litres</option>
                        <option value="2000-5000">2,000 - 5,000 litres</option>
                        <option value="5000-10000">5,000 - 10,000 litres</option>
                        <option value="10000-20000">10,000 - 20,000 litres</option>
                        <option value="over-20000">Over 20,000 litres</option>
                        <option value="unsure">Not sure yet</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="budget">Budget Range</label>
                      <select
                        id="budget"
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Budget Range</option>
                        <option value="under-200k">Under R200,000</option>
                        <option value="200k-500k">R200,000 - R500,000</option>
                        <option value="500k-1m">R500,000 - R1,000,000</option>
                        <option value="1m-2m">R1,000,000 - R2,000,000</option>
                        <option value="over-2m">Over R2,000,000</option>
                        <option value="flexible">Flexible - Depends on ROI</option>
                        <option value="financing">Need Financing Options</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="timeline">Preferred Timeline</label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Timeline</option>
                      <option value="urgent">Urgent - Within 2 weeks</option>
                      <option value="1-month">Within 1 month</option>
                      <option value="2-3-months">2-3 months</option>
                      <option value="3-6-months">3-6 months</option>
                      <option value="6-months-plus">6+ months</option>
                      <option value="research">Still researching options</option>
                    </select>
                  </div>
                </div>
                
                {/* Additional Information */}
                <div className="form-section">
                  <h3>Additional Information</h3>
                  <div className="form-group">
                    <label htmlFor="additionalNotes">Additional Notes & Specific Requirements</label>
                    <textarea
                      id="additionalNotes"
                      name="additionalNotes"
                      value={formData.additionalNotes}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Please share any specific requirements, concerns, or questions you have about your water business project..."
                    ></textarea>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="preferredContact">Preferred Contact Method</label>
                      <select
                        id="preferredContact"
                        name="preferredContact"
                        value={formData.preferredContact}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Contact Method</option>
                        <option value="phone">Phone Call</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="email">Email</option>
                        <option value="video-call">Video Call</option>
                        <option value="in-person">In-Person Meeting</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="hearAboutUs">How did you hear about us?</label>
                      <select
                        id="hearAboutUs"
                        name="hearAboutUs"
                        value={formData.hearAboutUs}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Source</option>
                        <option value="google-search">Google Search</option>
                        <option value="social-media">Social Media</option>
                        <option value="referral">Referral from Friend/Business</option>
                        <option value="existing-customer">Existing Customer</option>
                        <option value="trade-show">Trade Show/Exhibition</option>
                        <option value="advertisement">Advertisement</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* Submit Button */}
                <div className="form-submit">
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-large"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting Request...' : 'Submit Request for Quote'}
                  </button>
                  
                  <p className="form-note">
                    By submitting this form, you agree to be contacted by Pure Drop Technicals regarding your water business solution needs. 
                    We respect your privacy and will never share your information with third parties.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="request-benefits section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Why Request a Custom Quote?</h2>
            <p>Get personalized solutions designed specifically for your business goals</p>
          </div>
          
          <div className="benefits-grid">
            <div className="benefit-item">
              <div className="benefit-icon">
                <PaletteIcon size={48} className="benefit-icon-svg" />
              </div>
              <h3>Custom Design</h3>
              <p>Tailored solutions designed specifically for your location, budget, and business goals.</p>
            </div>
            
            <div className="benefit-item">
              <div className="benefit-icon">
                <ChartIcon size={48} className="benefit-icon-svg" />
              </div>
              <h3>ROI Analysis</h3>
              <p>Detailed financial projections and return on investment calculations for your specific scenario.</p>
            </div>
            
            <div className="benefit-item">
              <div className="benefit-icon">
                <TeamIcon size={48} className="benefit-icon-svg" />
              </div>
              <h3>Expert Consultation</h3>
              <p>One-on-one consultation with water business experts who understand the South African market.</p>
            </div>
            
            <div className="benefit-item">
              <div className="benefit-icon">
                <TrendingUpIcon size={48} className="benefit-icon-svg" />
              </div>
              <h3>Business Planning</h3>
              <p>Complete business setup guidance including legal requirements, marketing strategies, and growth planning.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientRequest;
